/* eslint-disable no-param-reassign */
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  MobileStepper,
  Slide,
  Step,
  StepLabel,
  Stepper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import makeStyles from "@mui/styles/makeStyles";
import React, { CSSProperties, useCallback, useEffect, useState } from "react";

const useStyles = makeStyles(() => ({
  root: {
    maxHeight: "95%",
    minHeight: ({ minHeight }: StyleProps) => minHeight ?? undefined,
    height: ({ height }: StyleProps) => height ?? undefined,
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
  },
  contentContainer: {
    display: "flex",
    maxHeight: "80%",
    height: ({ contentHeight }: StyleProps) => contentHeight ?? undefined,
  },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  renderBackButton: (
    step: number,
    handlePrevious: () => void,
    handleClose: () => void,
  ) => JSX.Element | null;
  renderNextButton: (
    step: number,
    handleNext: () => void,
    handleClose: () => void,
  ) => JSX.Element | null;
  steps: DialogStep[];
  onStepActive?: (step: number) => void;
  dialogTitle: string;
  hideSteps?: boolean;
  nextRef?: React.MutableRefObject<(() => void) | undefined>;
  prevRef?: React.MutableRefObject<(() => void) | undefined>;
  closeRef?: React.MutableRefObject<(() => void) | undefined>;
  extraDialogActions?: React.ReactNode;
};

type StyleProps = {
  minHeight?: CSSProperties["minHeight"];
  height?: CSSProperties["height"];
  contentHeight?: CSSProperties["height"];
};

export type DialogStep = {
  title: string;
  content: JSX.Element;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const GenericStepperDialog = ({
  open,
  onClose,
  steps,
  onStepActive,
  renderNextButton,
  renderBackButton,
  dialogTitle,
  hideSteps,
  minHeight,
  contentHeight,
  height,
  nextRef,
  prevRef,
  closeRef,
  extraDialogActions,
  ...rest
}: Props &
  StyleProps &
  Omit<
    DialogProps,
    "onClose" | "open" | "height" | "minHeight"
  >): JSX.Element => {
  const classes = useStyles({ minHeight, height, contentHeight });
  const [entered, setEntered] = useState(false);
  const [activeStep, setActiveStep] = useState(0); // Steps start at 0
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  useEffect(() => {
    if (!entered) return;
    onStepActive?.(activeStep);
  }, [activeStep, entered, onStepActive]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleNext = useCallback(() => {
    setActiveStep((old) => (old < steps.length - 1 ? old + 1 : old));
  }, [steps]);

  const handlePrevious = useCallback(() => {
    setActiveStep((old) => (old > 0 ? old - 1 : old));
  }, []);

  const getStepLabel = useCallback(() => {
    return steps[activeStep].title;
  }, [activeStep, steps]);

  useEffect(() => {
    if (nextRef) nextRef.current = handleNext;
    if (prevRef) prevRef.current = handlePrevious;
    if (closeRef) closeRef.current = handleClose;
  }, [closeRef, handleClose, handleNext, handlePrevious, nextRef, prevRef]);

  return (
    <Dialog
      onClose={handleClose}
      classes={{ paper: !isSmall ? classes.root : "" }}
      open={open}
      fullWidth
      fullScreen={isSmall}
      TransitionComponent={isSmall ? Transition : undefined}
      maxWidth={!isSmall ? "lg" : undefined}
      {...rest}
      TransitionProps={{
        onEntered: () => {
          setEntered(true);
        },

        onExited: () => {
          setEntered(false);
        },
      }}
    >
      <DialogTitle>
        {isSmall || hideSteps ? getStepLabel() : dialogTitle}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container className={classes.contentContainer}>
          {steps[activeStep]?.content ?? null}
        </Grid>
        {!isSmall && !hideSteps && (
          <Stepper activeStep={activeStep} style={{ padding: 24 }}>
            {steps.map((s) => (
              <Step key={s.title}>
                <StepLabel>{s.title}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </DialogContent>
      {!isSmall ? (
        <DialogActions>
          {extraDialogActions}
          {renderBackButton(activeStep, handlePrevious, handleClose)}
          {renderNextButton(activeStep, handleNext, handleClose)}
        </DialogActions>
      ) : (
        <>
          <DialogActions>{extraDialogActions}</DialogActions>
          <MobileStepper
            steps={steps.length}
            activeStep={activeStep}
            nextButton={renderNextButton(activeStep, handleNext, handleClose)}
            backButton={renderBackButton(
              activeStep,
              handlePrevious,
              handleClose,
            )}
            variant="dots"
            position="static"
          />
        </>
      )}
    </Dialog>
  );
};

export default GenericStepperDialog;
